import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormDatepicker } from '@ipfabric/ui-components/FormDatepicker/FormDatepicker';
import { FormIPSubnetField } from '@ipfabric/ui-components/FormIPSubnetField/FormIPSubnetField';
import { FormSegmentedButton } from '@ipfabric/ui-components/FormSegmentedButton/FormSegmentedButton';
import { FormSwitch } from '@ipfabric/ui-components/FormSwitch/FormSwitch';
import { FormTextArea } from '@ipfabric/ui-components/FormTextArea/FormTextArea';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import {
  type IPSubnetInputValueOrDraft,
  hasIPSubnetInputValueError,
  ipSubnetInputValueSchema,
} from '@ipfabric/ui-components/IPSubnetInput/IPSubnetInput.types';
import { Message } from '@ipfabric/ui-components/Message/Message';
import { SegmentButton } from '@ipfabric/ui-components/SegmentedButton/SegmentButton/SegmentButton';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { TextField } from '@ipfabric/ui-components/TextField/TextField';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { z } from 'zod';

interface EditUserDialogueProps {
  onClose: () => void;
  formType: 'add' | 'edit';
  onSubmit: (values: DeviceCredentialsFormValues) => void;
  initialValues?: DeviceCredentialsFormValues;
  hasExistingPrivateKey?: boolean;
  hasExistingPassword?: boolean;
}

export type AuthType = 'password' | 'ssh';

export interface DeviceCredentialsFormValues {
  authType: AuthType;
  username: string;
  password: string;
  retypePassword: string;
  expirationEnabled: boolean;
  expirationValue: Date | null;
  network: IPSubnetInputValueOrDraft;
  excludeNetworks: IPSubnetInputValueOrDraft;
  notes: string;
  syslog: boolean;
  privateKey: string;
  passphraseEnabled: boolean;
  passphrase: string;
}

const isValidPrivateKey = (val: string): boolean => {
  const trimmed = val.trim();
  return (
    /-----BEGIN\s+(?:\w+\s+)*PRIVATE KEY-----/.test(trimmed) &&
    !trimmed.includes('PUBLIC KEY')
  );
};

export const DeviceCredentialsDialog = ({
  onClose,
  formType,
  onSubmit,
  initialValues,
  hasExistingPrivateKey,
  hasExistingPassword,
}: EditUserDialogueProps) => {
  const [showReplaceKey, setShowReplaceKey] = useState(false);

  const { handleSubmit, formState, control, setValue } =
    useCustomForm<DeviceCredentialsFormValues>({
      defaultValues: (() => {
        const hasExpirationValue = !!initialValues?.expirationValue;

        return {
          authType:
            initialValues?.privateKey || hasExistingPrivateKey
              ? 'ssh'
              : 'password',
          username: initialValues?.username ?? '',
          password: initialValues?.password ?? '',
          retypePassword: initialValues?.retypePassword ?? '',
          expirationEnabled: hasExpirationValue,
          expirationValue: initialValues?.expirationValue ?? null,
          network: initialValues?.network ?? { ips: [] },
          excludeNetworks: initialValues?.excludeNetworks ?? { ips: [] },
          notes: initialValues?.notes ?? '',
          syslog: initialValues?.syslog ?? true,
          privateKey: initialValues?.privateKey ?? '',
          passphraseEnabled: initialValues?.passphraseEnabled ?? false,
          passphrase: initialValues?.passphrase ?? '',
        } satisfies DeviceCredentialsFormValues;
      })(),
      resolver: zodResolver(
        getValidationSchema(formType, {
          hasExistingPassword: !!hasExistingPassword,
          hasExistingPrivateKey: !!hasExistingPrivateKey,
        }),
      ),
    });

  const authType = useWatch({ control, name: 'authType' });
  const expirationEnabled = useWatch({ control, name: 'expirationEnabled' });
  const expirationValue = useWatch({ control, name: 'expirationValue' });
  const passphraseEnabled = useWatch({ control, name: 'passphraseEnabled' });

  useEffect(() => {
    if (authType === 'password') {
      setValue('privateKey', '');
      setValue('passphrase', '');
      setValue('passphraseEnabled', false);
      setShowReplaceKey(false);
    } else {
      setValue('password', '');
      setValue('retypePassword', '');
    }
  }, [authType, setValue]);

  useEffect(() => {
    if (expirationValue) {
      setValue('expirationEnabled', true);
    }
  }, [expirationValue, setValue]);

  const showPrivateKeyPlaceholder =
    formType === 'edit' && hasExistingPrivateKey && !showReplaceKey;

  return (
    <SidePanel
      title={
        formType === 'add' ? 'Add new CLI credential' : 'Edit CLI credential'
      }
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            dataTestId='device-credentials-save'
            disabled={!formState.isDirty}
            icon='fa-floppy-disk'
            loading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
            type='submit'
            variant='primary'
          >
            Save
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card className='flex flex-col gap p-sm'>
          <FormSegmentedButton.Single<DeviceCredentialsFormValues, AuthType>
            control={control}
            name='authType'
            label='Authentication type'
          >
            <SegmentButton value='password' className='flex-1'>
              Username / Password
            </SegmentButton>
            <SegmentButton value='ssh' className='flex-1'>
              SSH credentials
            </SegmentButton>
          </FormSegmentedButton.Single>
          {authType === 'ssh' && (
            <Message
              variant='plain'
              type='info'
              title='Security notice'
              message='An SSH private key is sensitive information. When you add it here, IP Fabric stores it encrypted and uses it to authenticate to matching network devices. Only add keys that are approved for this purpose and limited to the required devices.'
            />
          )}
          <FormTextField
            control={control}
            disabled={formType === 'edit'}
            name='username'
            label='Username'
            type='text'
            className='flex-1'
          />
          {authType === 'password' ? (
            <>
              <FormTextField
                control={control}
                name='password'
                label='Password'
                type='password'
                className='flex-1'
              />
              <FormTextField
                control={control}
                name='retypePassword'
                label='Retype password'
                type='password'
                className='flex-1'
              />
            </>
          ) : (
            <>
              {showPrivateKeyPlaceholder ? (
                <div className='flex items-end gap-xs'>
                  <TextField
                    type='text'
                    label='SSH private key'
                    value='SSH key is in use'
                    disabled
                    className='flex-1'
                  />
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setShowReplaceKey(true)}
                  >
                    Replace SSH key
                  </Button>
                </div>
              ) : (
                <>
                  <FormTextArea
                    control={control}
                    name='privateKey'
                    label='SSH private key'
                    labelTooltip='Insert contents of your private SSH key file'
                  />
                  <div className='flex flex-col gap-xs'>
                    <FormSwitch
                      control={control}
                      name='passphraseEnabled'
                      description='Use passphrase'
                      className='flex-1'
                    />
                    {passphraseEnabled && (
                      <FormTextField
                        control={control}
                        name='passphrase'
                        label='SSH key passphrase'
                        type='password'
                        className='flex-1'
                      />
                    )}
                  </div>
                </>
              )}
            </>
          )}
          <FormIPSubnetField
            control={control}
            name='network'
            label='Use in subnets'
            type='cidr'
          />
          <FormIPSubnetField
            control={control}
            name='excludeNetworks'
            label="Don't use in subnets"
            type='cidr'
          />
          <div className='flex flex-col gap-xs'>
            <FormSwitch
              control={control}
              name='expirationEnabled'
              description={'Set expiration date'}
              className='flex-1'
            />
            {expirationEnabled && (
              <FormDatepicker
                className='ml-12'
                control={control}
                name='expirationValue'
              />
            )}
          </div>
          <FormSwitch
            control={control}
            name='syslog'
            description='Use for configuration management'
          />
          <FormTextArea control={control} name='notes' label='Notes' />
        </Card>
      </form>
    </SidePanel>
  );
};

interface ValidationOptions {
  hasExistingPassword?: boolean;
  hasExistingPrivateKey?: boolean;
}

const getValidationSchema = (
  formType: 'add' | 'edit',
  opts: ValidationOptions = {},
) => {
  const { hasExistingPassword = false, hasExistingPrivateKey = false } = opts;

  return (
    z
      .object({
        authType: z.enum(['password', 'ssh']),
        username: z.string().optional(),
        expirationEnabled: z.boolean(),
        expirationValue: z.date().nullable(),
        network: ipSubnetInputValueSchema,
        excludeNetworks: ipSubnetInputValueSchema,
        notes: z.string(),
        syslog: z.boolean(),
        passphraseEnabled: z.boolean(),
        passphrase: z.string(),
        privateKey: z.string().refine((val) => !val || isValidPrivateKey(val), {
          message:
            'Please enter a valid SSH private key (not a public key or certificate)',
        }),
        password: z.string(),
        retypePassword: z.string(),
      })
      // Password mode: password required (unless editing a credential that already has one)
      .refine(
        (data) => {
          if (data.authType !== 'password') {
            return true;
          }
          return (
            !!data.password || (formType === 'edit' && hasExistingPassword)
          );
        },
        {
          message: 'Password is required',
          path: ['password'],
        },
      )
      // Password mode: retypePassword required when entering a new password
      .refine(
        (data) => {
          if (data.authType !== 'password' || !data.password) {
            return true;
          }
          return !!data.retypePassword;
        },
        {
          message: 'Please confirm your password',
          path: ['retypePassword'],
        },
      )
      // Password mode: passwords must match
      .refine(
        (data) => {
          if (data.authType !== 'password' || !data.password) {
            return true;
          }
          return data.password === data.retypePassword;
        },
        {
          message: "Passwords don't match",
          path: ['retypePassword'],
        },
      )
      // SSH mode: private key required (unless editing a credential that already has one)
      .refine(
        (data) => {
          if (data.authType !== 'ssh') {
            return true;
          }
          return (
            !!data.privateKey || (formType === 'edit' && hasExistingPrivateKey)
          );
        },
        {
          message: 'SSH private key is required',
          path: ['privateKey'],
        },
      )
      // SSH mode: passphrase required when passphraseEnabled and a private key is involved
      .refine(
        (data) => {
          if (data.authType !== 'ssh' || !data.passphraseEnabled) {
            return true;
          }
          // When keeping the existing key (not replacing), passphrase is preserved server-side
          if (
            formType === 'edit' &&
            hasExistingPrivateKey &&
            !data.privateKey
          ) {
            return true;
          }
          return !data.privateKey || !!data.passphrase;
        },
        {
          message: 'Passphrase is required',
          path: ['passphrase'],
        },
      )
      .superRefine((data, ctx) => {
        if (hasIPSubnetInputValueError(data.network)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: data.network.message,
            path: ['network'],
          });
        }

        if (hasIPSubnetInputValueError(data.excludeNetworks)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: data.excludeNetworks.message,
            path: ['excludeNetworks'],
          });
        }
      })
  );
};

// Exported for unit tests only
export const getValidationSchemaForTest = getValidationSchema;
